import React, { useState } from "react";
import { Tooltip } from "@multiagentbook/ui";
import { Dimension, calculateDimensionScore } from "./useFrameworkData";

// Define a standard order for dimensions to ensure consistency across frameworks
const standardDimensionOrder = [
  "accessibility",
  "documentation",
  "ease_of_use",
  "community",
  "flexibility",
  "interoperability",
  "reliability",
  "performance",
  "security",
  "maturity",
];

interface ScoreBarsProps {
  dimensions: Dimension[];
}

const ScoreBars: React.FC<ScoreBarsProps> = ({ dimensions }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Sort dimensions by the standard order instead of by score
  const sortedDimensions = [...dimensions].sort((a, b) => {
    const indexA = standardDimensionOrder.indexOf(a.dimension_slug);
    const indexB = standardDimensionOrder.indexOf(b.dimension_slug);

    // If both dimensions are in our standard order, sort by that order
    if (indexA >= 0 && indexB >= 0) {
      return indexA - indexB;
    }

    // If only one is in the standard order, prioritize it
    if (indexA >= 0) return -1;
    if (indexB >= 0) return 1;

    // If neither is in our standard order, sort alphabetically by name
    return a.name.localeCompare(b.name);
  });

  // Define a fixed height for better control
  const containerHeight = 50; // pixels

  return (
    <div className="flex flex-col">
      <div
        className="flex items-end gap-1"
        style={{ height: `${containerHeight}px` }}
      >
        {sortedDimensions.map((dim, index) => {
          // Calculate absolute height in pixels instead of percentages
          // This ensures more precise control over the heights
          const heightPx = Math.max(
            4,
            (calculateDimensionScore(dim) / 10) * containerHeight
          );
          const isHovered = hoveredIndex === index;

          return (
            <Tooltip
              key={dim.dimension_slug}
              title={
                <div className="text-xs">
                  <div className="font-bold mb-1">{dim.name}</div>
                  <div className="bg-accent text-white px-2 py-1 rounded inline-block mb-1">
                    Score: {calculateDimensionScore(dim).toFixed(1)}
                  </div>
                  <div className="text-xs opacity-80 mt-1 max-w-48">
                    {dim.dimension_justification.length > 100
                      ? dim.dimension_justification.substring(0, 100) + "..."
                      : dim.dimension_justification}
                  </div>
                </div>
              }
              placement="top"
            >
              <div
                className="flex-1 flex flex-col items-center cursor-pointer relative"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div
                  className={`w-full transition-all duration-300 rounded-t-sm border ${
                    isHovered
                      ? "bg-accent border-accent"
                      : "bg-secondary border-secondary/70"
                  }`}
                  style={{
                    height: `${heightPx}px`,
                    transform: isHovered ? "scaleY(1.1)" : "scaleY(1)",
                    transformOrigin: "bottom",
                    zIndex: isHovered ? 10 : 1,
                  }}
                ></div>
              </div>
            </Tooltip>
          );
        })}
      </div>

      {/* Dimension abbreviations */}
      <div className="flex gap-1 mt-1">
        {sortedDimensions.map((dim, index) => (
          <div
            key={dim.dimension_slug}
            className={`flex-1 text-center text-[8px] overflow-hidden whitespace-nowrap ${
              hoveredIndex === index
                ? "text-accent font-bold"
                : "text-secondary"
            }`}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {dim.name.substring(0, 2)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScoreBars;
